import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { customerCreateSchema } from '@fin/shared/schemas';
import { EMIRATES } from '@fin/shared/enums';
import { BRAND } from '@fin/shared/brand';

import { Button, Card, Field, Modal, PageHeader, inputClass, useDisclosure } from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';
import { useCreateCustomer, useCustomers, type Customer } from '../lib/queries';

export function CustomersPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const setQ = (value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('q', value);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  };
  const list = useCustomers(q);
  const modal = useDisclosure();
  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Your contacts and service addresses"
        icon={PageIcon.customers}
        accent={PageAccent.customers}
        action={<Button variant="white" onClick={modal.onOpen}>+ Add customer</Button>}
      />
      <div className="mb-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name (prefix) or phone…"
          className={inputClass}
        />
      </div>
      {list.isPending && <p className="text-stone-500">Loading…</p>}
      {list.isError && <p style={{ color: BRAND.danger }}>Failed to load: {(list.error as Error).message}</p>}
      {list.data && (
        <Card>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Area</th>
                <th className="px-4 py-3">Emirate</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c: Customer) => (
                <tr key={c.id} className="border-b border-stone-100 last:border-0">
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/customers/${c.id}`} className="hover:underline" style={{ color: BRAND.primary }}>
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-stone-600">{c.phone}</td>
                  <td className="px-4 py-3 text-stone-600">{c.address.area}</td>
                  <td className="px-4 py-3 text-stone-600">{c.address.emirate.replace('_', ' ')}</td>
                </tr>
              ))}
              {list.data.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-stone-500">
                    No customers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </Card>
      )}
      <Modal open={modal.open} onClose={modal.onClose} title="Add customer">
        <CreateCustomerForm onClose={modal.onClose} />
      </Modal>
    </>
  );
}

function CreateCustomerForm({ onClose }: { onClose: () => void }) {
  const create = useCreateCustomer();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+971');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [building, setBuilding] = useState('');
  const [area, setArea] = useState('');
  const [emirate, setEmirate] = useState<(typeof EMIRATES)[number]>('dubai');
  const [makani, setMakani] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const body = customerCreateSchema.parse({
        name,
        phone,
        whatsappPhone: whatsapp,
        email: email || undefined,
        address: {
          building,
          area,
          emirate,
          makaniNumber: makani || undefined,
        },
      });
      await create.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err instanceof z.ZodError ? err.issues.map((i) => i.message).join(', ') : err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {error && (
        <div
          role="alert"
          className="rounded border px-3 py-2 text-xs"
          style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
        >
          {error}
        </div>
      )}
      <Field label="Name">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Phone (+971…)">
          <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </Field>
        <Field label="WhatsApp (+971…)">
          <input className={inputClass} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} required placeholder="+971..." />
        </Field>
      </div>
      <Field label="Email (optional)">
        <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <hr className="border-stone-200" />
      <Field label="Building / villa">
        <input className={inputClass} value={building} onChange={(e) => setBuilding(e.target.value)} required />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Area / community">
          <input className={inputClass} value={area} onChange={(e) => setArea(e.target.value)} required />
        </Field>
        <Field label="Emirate">
          <select
            className={inputClass}
            value={emirate}
            onChange={(e) => setEmirate(e.target.value as (typeof EMIRATES)[number])}
          >
            {EMIRATES.map((e) => (
              <option key={e} value={e}>
                {e.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Makani number (optional)" hint="8 digits or 4-4 format">
        <input className={inputClass} value={makani} onChange={(e) => setMakani(e.target.value)} />
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Create customer'}
        </Button>
      </div>
    </form>
  );
}
